/**
 * JSON Schema generation utilities
 * Shared logic for deriving JSON schemas from payload structures
 */

/**
 * Generate a JSON schema from a JSON payload
 * Inspects the payload structure and generates a matching schema
 */
export const generateSchemaFromPayload = (payload: any): string => {
  const inferType = (value: any): string => {
    if (value === null) return 'null';
    if (Array.isArray(value)) return 'array';
    return typeof value;
  };

  const buildSchemaFromObject = (obj: any): any => {
    if (typeof obj !== 'object' || obj === null || Array.isArray(obj)) {
      return { type: inferType(obj) };
    }

    const properties: any = {};
    const required: string[] = [];

    for (const [key, value] of Object.entries(obj)) {
      if (Array.isArray(value)) {
        const firstItem = value.length > 0 ? value[0] : null;
        properties[key] = {
          type: 'array',
          items: typeof firstItem === 'object' && firstItem !== null && !Array.isArray(firstItem)
            ? buildSchemaFromObject(firstItem)
            : { type: inferType(firstItem) }
        };
      } else if (typeof value === 'object' && value !== null) {
        properties[key] = buildSchemaFromObject(value);
      } else {
        properties[key] = { type: inferType(value) };
      }
      required.push(key);
    }

    return {
      type: 'object',
      properties,
      required
    };
  };

  const schema = buildSchemaFromObject(payload);
  return JSON.stringify(schema, null, 2);
};

/**
 * Extract agentIdentity schema from a payload in the format needed for channel configuration
 * Returns a schema formatted for the channel edit request schema field
 * @param payload - The payload to extract the identity schema from
 * @param fieldName - The name of the identity field (defaults to 'agentIdentity')
 */
export const extractAgentIdentitySchema = (payload: any, fieldName: string = 'agentIdentity'): string => {
  // Try to find identity field in common locations
  let identityData = null;
  
  // Check if payload has the identity field directly
  if (payload && typeof payload === 'object') {
    if (payload[fieldName]) {
      identityData = payload[fieldName];
    } 
    // Check in metadata (A2A protocol)
    else if (payload.metadata) {
      for (const key in payload.metadata) {
        if (key.includes('agent-identity') && payload.metadata[key]) {
          identityData = payload.metadata[key];
          break;
        }
      }
    }
    // Check in _meta (MCP protocol) - use the configured field name
    else if (payload._meta && payload._meta[fieldName]) {
      identityData = payload._meta[fieldName];
    }
  }

  // If no identity data found, return empty schema
  if (!identityData) {
    return JSON.stringify({
      "type": "object",
      "properties": {
        [fieldName]: {
          "type": "object",
          "properties": {}
        }
      },
      "required": []
    }, null, 2);
  }

  // Build schema from agentIdentity
  const inferType = (value: any): string => {
    if (value === null) return 'null';
    if (Array.isArray(value)) return 'array';
    return typeof value;
  };

  const buildSchemaFromObject = (obj: any): any => {
    if (typeof obj !== 'object' || obj === null || Array.isArray(obj)) {
      return { type: inferType(obj) };
    }

    const properties: any = {};
    const required: string[] = [];

    for (const [key, value] of Object.entries(obj)) {
      if (Array.isArray(value)) {
        const firstItem = value.length > 0 ? value[0] : null;
        properties[key] = {
          type: 'array',
          items: typeof firstItem === 'object' && firstItem !== null && !Array.isArray(firstItem)
            ? buildSchemaFromObject(firstItem)
            : { type: inferType(firstItem) }
        };
      } else if (typeof value === 'object' && value !== null) {
        properties[key] = buildSchemaFromObject(value);
      } else {
        properties[key] = { type: inferType(value) };
      }
      required.push(key);
    }

    return {
      type: 'object',
      properties,
      required
    };
  };

  const agentIdentitySchema = buildSchemaFromObject(identityData);

  // Wrap in the channel configuration format
  const channelSchema = {
    "type": "object",
    "properties": {
      [fieldName]: agentIdentitySchema
    },
    "required": []
  };

  return JSON.stringify(channelSchema, null, 2);
};
